import * as React from 'react';
import { useStore, useDispatch } from 'react-redux';
import { SelectedResetter } from '../../../../components/drag-select/SelectableGroup';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PagedList } from '../../../../components/paged-list/PagedList';
import { useContextMenu } from "../../../../hooks/use-context-menu";
import { PagedApiResult } from '../../../../models/utilities/paged-request';
import { StatusCode } from '../../../../utils/api';
import { ApiEndpoints, PageNames } from '../../../../utils/app-constants';
import { CommunicationsRightClickMenu } from '../components/CommunicationsRightClickMenu';
import { SendNotificationsFlyout } from '../components/SendNotificationsFlyout';
import TruckCommunicationsRow from '../components/TruckCommunicationsRow';
import { TruckWithNotifications } from '../../../../transportation/models/communications/truck-delivery-schedule';
import { ShipmentStatus } from '../../../../transportation/models/enums/shipment-status';
import { TruckSearchFilters } from '../../../../transportation/models/filters/truck-search-filters';
import { getCommunicationsTruckSearchFilters } from '../../../../transportation/selectors';
import { TransportationSvc } from '../../../../transportation/service';
import { ShipmentType } from '../../../../transportation/models/enums/_index';
import { DocumentRunLogBulk } from '../../../../transportation/models/shared/document-runlog-bulk';
import { DocumentRunLogExportBulk } from '../../../../transportation/models/shared/document-runlog-export-bulk';
import '../../../../transportation/styles.scss';
import { getDocumentHelper } from '../../../../actions/utility-actions';

const ContextMenu = ({ menu, onShow, onHide }: any) => {
    const { xPos, yPos, showMenu } = useContextMenu(menu, onShow, onHide);
    return showMenu
        ? <div
            className="right-click menu-container"
            style={{
                top: yPos,
                left: xPos,
                opacity: 1
            }}>
            {menu}
        </div>
        : null;
};

interface IProps {

}

export const CommunicationsTrucksScene = (props: IProps) => {

    const store = useStore();
    const dispatch = useDispatch();

    const filters = ['byTruckId', 'byStatus', 'byName', 'byCutoff', 'byDealer'];
    const columns = ['truckId', 'status', 'name', 'driver', 'cutoff', 'psSent', 'pdSent', 'fdSent'];
    const removeClosedFilters = (filters: TruckSearchFilters) => {
        var statusFilters = filters.filters.find(f => f.name === 'Status');
        var closedStatus = statusFilters?.opts.find(o => o.qsValue === ShipmentStatus.Closed);
        if (closedStatus) {
            statusFilters?.opts.splice(statusFilters?.opts.indexOf(closedStatus), 1);
        }
    }

    const selectedResetter = new SelectedResetter();

    const [isMenuShown, setIsMenuShown] = React.useState<any>();
    const [selectedNotificationType, setSelectedNotificationType] = React.useState<any>();
    const [isSendOpen, setIsSendOpen] = React.useState(false);
    const [hasSelected, setHasSelected] = React.useState(false);
    const [selecteds, setSelecteds] = React.useState<TruckWithNotifications[]>([]);
    const [filterParams, setFilterParams] = React.useState(() => {
        var res = getCommunicationsTruckSearchFilters(store.getState(), columns, filters);
        removeClosedFilters(res);
        res.baseUrl = ApiEndpoints.TRANSPORTATION_COMMUNICATIONS_TRUCKS_SEARCH;
        return res;
    });

    const [service] = React.useState(new TransportationSvc());
    const [deliveries, setDeliveries] = React.useState(() => {
        const initial = new PagedApiResult<TruckWithNotifications>();
        initial.pagination = filterParams.paginationInfo;
        initial.pagination.reusePaging = true;
        return initial;
    });

    /**
     * OnSearch handler
     * @param filters The list of filters to apply to the search
     */
    const onSearch = async (filters: TruckSearchFilters) => {
        resetSelected();
        const response = await service.search(TruckWithNotifications, filters);
        if (response.status === StatusCode.Success) {
            setDeliveries(response.data);
            scrollTo();
        }

        setFilterParams(filters);
    };

    const onParamsUpdate = (itm: any) => {
        setFilterParams(itm);
    };

    const onParamsReset = () => {
        resetSelected();
        const tmp = new TruckSearchFilters(columns, filters);
        removeClosedFilters(tmp);
        tmp.baseUrl = ApiEndpoints.TRANSPORTATION_COMMUNICATIONS_TRUCKS_SEARCH;
        setFilterParams(tmp);
    };

    const resetSelected = () => {
        const tmpDeliveries = { ...deliveries };

        tmpDeliveries.results.forEach((itm: TruckWithNotifications) => {
            itm.isChecked = false;
        });

        setSelecteds([]);
        setDeliveries(tmpDeliveries);
        setHasSelected(false);
        selectedResetter.reset();
    }

    /**
     * Handler for when the user is done drag selecting items
     * @param evt The draggable event with selected items
     */
    const onDragComplete = (evt: any) => {

        const tmpDeliveries = { ...deliveries };

        tmpDeliveries.results.forEach((itm: TruckWithNotifications) => {
            itm.isChecked = false;
        });

        const tmpList: TruckWithNotifications[] = [];

        for (let i = 0; i < evt.length; i++) {
            const evtItem = evt[i];

            for (let j = 0; j < tmpDeliveries.results.length; j++) {
                if (tmpDeliveries.results[j].id === evtItem.props.truck.id) {
                    tmpDeliveries.results[j].isChecked = true;
                    tmpList.push(tmpDeliveries.results[j]);
                    break;
                }
            }
        }

        setSelecteds(tmpList);
        setDeliveries(tmpDeliveries);
        setHasSelected(tmpList && tmpList.length > 0);
    };

    const sendNotifications = async (notificationType: any) => {
        setSelectedNotificationType(notificationType);
        setIsSendOpen(true);
    };

    const downloadRunLogs = async () => {

        var shipmentIds = selecteds.map(s => s.id);
        const req = new DocumentRunLogBulk(shipmentIds, ShipmentType.Truck);
        dispatch(getDocumentHelper(req));
    };

    const downloadRunLogExports = async () => {

        var shipmentIds = selecteds.map(s => s.id);
        const req = new DocumentRunLogExportBulk(shipmentIds, ShipmentType.Truck);
        dispatch(getDocumentHelper(req));
    };

    /**
     * Close the send notifications flyout
     */
    const closeSendNotificationsFlyout = async (refresh: boolean) => {
        setIsSendOpen(false);
        if (refresh) {
            await onSearch(filterParams);
        }
    };

    /**
     * Display a right click menu based on the selections
     * @returns A right click menu to show
     */
    const getMenuContext = () => {
        if (hasSelected) {
            return <CommunicationsRightClickMenu
                sendNotifications={sendNotifications}
                downloadRunLogs={downloadRunLogs}
                downloadRunLogExports={downloadRunLogExports}
            />;
        }
        else {
            return null;
        }
    }

    return (<PageContent classes="deliveries-list" pageName={PageNames.TRANSPORTATION_TRUCK_COMMUNICATIONS}>
        <PageBody>
            <PagedList
                    noResultsMessage="No trucks found, please search again."
                    searchCallback={onSearch}
                    data={deliveries}
                    filterProps={filterParams}
                    selectableClass=""
                    onFilterUpdate={onParamsUpdate}
                    onReset={onParamsReset}
                    selectedResetter={selectedResetter}
                    enabledDrag={!isMenuShown}
                    onDragComplete={onDragComplete}
                    showSelectAll={true}
                    hidePager={true}
                    rowRenderer={(truck: TruckWithNotifications, extraProps: any) =>
                        <TruckCommunicationsRow
                            key={`truck-${truck.id}`}
                            truck={truck}
                            {...extraProps} />
                    } />
            <ContextMenu menu={getMenuContext()} onShow={() => { setIsMenuShown(true) }} onHide={() => { setIsMenuShown(false) }} />

            <SendNotificationsFlyout
                shipments={selecteds}
                isOpen={isSendOpen}
                onClose={closeSendNotificationsFlyout}
                notificationType={selectedNotificationType}
            />
        </PageBody>
    </PageContent>);

};
